import axios from '@axios'

/**
 * Resolve a tabela de preço **efetiva** para a venda em curso no PDV/ATM.
 *
 * O `pages/pdv/payBox.priceTable` é setado a partir do cliente identificado
 * (ou da configuração do caixa). Em alguns cenários esse valor não pode ser
 * usado diretamente para a venda:
 *
 *   - Tabela com `active = false` → bloqueada para uso
 *   - Estamos no ATM e a tabela não tem `activeAtm = true` → não autorizada no canal
 *
 * Nesses casos, a regra do produto é usar a tabela default da loja
 * (`isDefault = true && active = true`). Esse módulo cacheia a default em
 * memória e expõe um getter síncrono que combina `state.priceTable` da venda
 * com a default para retornar a tabela que **deve reger** os cálculos.
 *
 * IMPORTANTE: este módulo apenas **resolve** a tabela; ele não modifica
 * `pages/pdv/payBox.priceTable`. Os consumidores (ex.: SpendAndGet) leem o
 * getter `effectivePriceTable` quando precisam aplicar regras de canal
 * (ActiveOnline, ActiveAtm, etc).
 */

const getInitialState = () => ({
  /** Snapshot da tabela default ativa, carregada via API. */
  defaultPriceTable: null,
  /** StoreId da última carga, para invalidar o cache se o caixa mudar de loja. */
  defaultPriceTableStoreId: null,
  loading: false,
  loadedAt: null,
})

export default {
  namespaced: true,

  state: getInitialState(),

  getters: {
    /**
     * Decide qual tabela rege a venda atual aplicando, em ordem:
     *   1. Se a tabela atual da venda for nula → default
     *   2. Se a tabela atual for explicitamente `active === false` → default
     *   3. Se estamos no ATM e a tabela atual não tiver `activeAtm === true`
     *      → default
     *   4. Caso contrário, a tabela atual da venda
     *
     * IMPORTANTE: para `active` a regra trata `undefined`/`null` como **válido**
     * (a tabela passa a regra). Isso protege casos em que o DTO da venda
     * persistida vem sem alguns flags (legado / endpoints que ainda não foram
     * atualizados) — não queremos alterar uma venda já carregada por causa de
     * payload incompleto. Apenas `false` explícito força o fallback.
     *
     * Para `activeAtm` só `true` libera a tabela, a mesma regra que o backend
     * usa para precificar os itens do ATM (`PayBoxSkuAtm`/`PayBoxSkuByEan`) e
     * para gravar a venda. Tabela com `activeAtm` nulo não está liberada no ATM.
     *
     * Quando a default ainda não foi carregada e há fallback necessário,
     * retorna `null` — caller deve aguardar `ensureDefaultPriceTable` e
     * ler novamente.
     */
    effectivePriceTable: (state, _getters, rootState, rootGetters) => {
      const sale = rootState?.pages?.pdv?.payBox?.priceTable ?? null
      const isAtm = !!rootGetters['pages/pdv/payBoxConfiguration/isPdvAtm']
      const fallback = state.defaultPriceTable

      if (!sale) return fallback
      if (sale.active === false) return fallback
      if (isAtm && sale.activeAtm !== true) return fallback

      return sale
    },

    /**
     * Indica se o getter `effectivePriceTable` precisou aplicar fallback para
     * a default — útil para sinalizar ao operador que a tabela escolhida não
     * pode reger a venda no canal atual.
     */
    didFallbackToDefault: (state, getters, rootState, rootGetters) => {
      const sale = rootState?.pages?.pdv?.payBox?.priceTable ?? null
      const isAtm = !!rootGetters['pages/pdv/payBoxConfiguration/isPdvAtm']

      if (!sale) return state.defaultPriceTable !== null
      if (sale.active === false) return state.defaultPriceTable !== null
      if (isAtm && sale.activeAtm !== true) return state.defaultPriceTable !== null

      return false
    },
  },

  mutations: {
    SET_DEFAULT_PRICE_TABLE(state, { priceTable, storeId }) {
      state.defaultPriceTable = priceTable || null
      state.defaultPriceTableStoreId = storeId ?? null
      state.loadedAt = Date.now()
    },
    SET_LOADING(state, value) {
      state.loading = value
    },
    CLEAN_STATE(state) {
      Object.assign(state, getInitialState())
    },
  },

  actions: {
    /**
     * Garante que a tabela default da loja informada esteja carregada em
     * cache. Faz a chamada apenas quando a loja muda ou quando `force` é
     * `true`. Falha silenciosa: se o endpoint não responder, o getter
     * `effectivePriceTable` continua devolvendo a tabela atual da venda
     * (sem fallback) — o consumidor pode interpretar a ausência como
     * "manter o comportamento legado".
     */
    async ensureDefaultPriceTable({ commit, state }, { storeId, force = false } = {}) {
      if (!storeId) return null

      if (!force && state.defaultPriceTableStoreId === storeId && state.defaultPriceTable) {
        return state.defaultPriceTable
      }

      try {
        commit('SET_LOADING', true)
        const { data } = await axios.get(
          `/api/sales/pay-box-sku/store/${storeId}/default-price-table`
        )
        commit('SET_DEFAULT_PRICE_TABLE', { priceTable: data || null, storeId })
        return data || null
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[effectivePriceTable] fetch failed:', err?.message || err)
        commit('SET_DEFAULT_PRICE_TABLE', { priceTable: null, storeId })
        return null
      } finally {
        commit('SET_LOADING', false)
      }
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
  },
}
